import { useCallback, useState } from "react";
import { lsLoad } from "../../tools/Storage";
import StorageEnum from "../../enums/Storage.enum";
import type EditMarketProperties from "./EditMarket.properties.ts";
import type {
  MarketComponentMarketComponentIO,
  MarketMarketIO,
} from "../../generated/fresh-guard-api";
import { EditMarketContext } from "./EditMarket.context.tsx";
import { useWebSocket } from "../../web-socket/useWebSocket.ts";

export const EditMarketProvider = (props: EditMarketProperties) => {
  const [idsOfNewMarketComponents, setIdsOfNewMarketComponents] = useState<
    number[]
  >([]);
  const [enableDragAndDropToolbar, setEnableDragAndDropToolbar] =
    useState(false);
  const [enableDragAndDropMarketArea, setEnableDragAndDropMarketArea] =
    useState(false);
  const [enableZoom, setEnableZoom] = useState(true);
  const [marketComponents, setMarketComponents] = useState<
    MarketComponentMarketComponentIO[]
  >(lsLoad(StorageEnum.MarketComponents));
  const [markets, setMarkets] = useState<MarketMarketIO[]>([]);
  const [marketComponentsEdited, setMarketComponentsEdited] = useState<
    MarketComponentMarketComponentIO[]
  >([]);
  const [isInEditMode, setIsInEditMode] = useState(false);

  // WebSocket callback:
  // With the new "signal only" strategy, the actual data is loaded via the API
  // (React Query), so we no longer need to process any payload here.
  const onStatusUpdate = useCallback(
    (_marketComponents: MarketComponentMarketComponentIO[]) => {
      // keep parameter for type compatibility; explicitly mark as used
      void _marketComponents;
      // no-op: data is refreshed via the API
    },
    [],
  );

  const { sendStatus } = useWebSocket(onStatusUpdate);
  const handleMarketComponents = useCallback(
    (marketComponents: MarketComponentMarketComponentIO[]) => {
      // Update local state
      setMarketComponents(marketComponents);
      // Send a WebSocket signal: "something changed for this market"
      void sendStatus(marketComponents);
    },
    [sendStatus],
  );

  const addIdOfMarketComponents = useCallback(
    (id: number) => {
      setIdsOfNewMarketComponents([...idsOfNewMarketComponents, id]);
    },
    [idsOfNewMarketComponents],
  );

  const clearIdsOfMarketComponents = () => {
    setIdsOfNewMarketComponents([]);
  };

  const addMarketComponent = useCallback(
    (marketComponent: MarketComponentMarketComponentIO) => {
      setMarketComponentsEdited((prev) => {
        const withoutOld = prev.filter(
          (item) => item.id !== marketComponent.id,
        );
        return [...withoutOld, marketComponent];
      });

      const withoutOld = marketComponents.filter(
        (item) => item.id !== marketComponent.id,
      );

      handleMarketComponents([...withoutOld, marketComponent]);
    },
    [handleMarketComponents, marketComponents],
  );

  const updateMarketComponent = (
    marketComponent: MarketComponentMarketComponentIO,
  ) => {
    setMarketComponentsEdited((prev) => {
      const withoutOld = prev.filter((item) => item.id !== marketComponent.id);
      return [...withoutOld, marketComponent];
    });
    setMarketComponents((prev) => {
      const withoutOld = prev.filter((item) => item.id !== marketComponent.id);
      return [...withoutOld, marketComponent];
    });
  };

  const updateMarketComponentStatus = useCallback(
    (marketComponent: MarketComponentMarketComponentIO) => {
      const withoutOld = marketComponents.filter(
        (item) => item.id !== marketComponent.id,
      );

      handleMarketComponents([...withoutOld, marketComponent]);
    },
    [handleMarketComponents, marketComponents],
  );

  const removeMarketComponent = (
    marketComponent: MarketComponentMarketComponentIO | undefined,
  ) => {
    const marketComponentsWithoutRemoved = marketComponents.filter(
      (item) => marketComponent?.id && item.id !== marketComponent.id,
    );
    handleMarketComponents(marketComponentsWithoutRemoved);

    const marketComponentsEditedWithoutRemoved = marketComponentsEdited.filter(
      (item) => marketComponent?.id && item.id !== marketComponent.id,
    );
    setMarketComponentsEdited(marketComponentsEditedWithoutRemoved);
  };

  const initMarket = useCallback((markets: MarketMarketIO[]) => {
    setMarkets(markets);
  }, []);

  const initMarketComponents = useCallback(
    (marketComponents: MarketComponentMarketComponentIO[]) => {
      handleMarketComponents(marketComponents);
    },
    [handleMarketComponents],
  );

  const resetMarketComponentsEdited = () => {
    setMarketComponentsEdited([]);
  };

  return (
    <EditMarketContext.Provider
      value={{
        idsOfNewMarketComponents,
        addIdOfMarketComponents,
        clearIdsOfMarketComponents,
        enableDragAndDropToolbar,
        enableDragAndDropMarketArea,
        enableZoom,
        markets,
        marketComponents,
        marketComponentsEdited,
        setEnableDragAndDropToolbar,
        setEnableDragAndDropMarketArea,
        setEnableZoom,
        addMarketComponent,
        removeMarketComponent,
        updateMarketComponent,
        updateMarketComponentStatus,
        initMarket,
        initMarketComponents,
        resetMarketComponentsEdited,
        isInEditMode,
        setIsInEditMode,
      }}
    >
      {props.children}
    </EditMarketContext.Provider>
  );
};
